import { useEffect, useMemo, useState } from "react";
import { Bell, Check, Globe, MessageSquare, X } from "lucide-react";
import {
  Box,
  Badge,
  Card,
  CardContent,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import apiService from "../service/Api";
import ProducerAssignmentDialog from "../pages/crew-management/components/ProducerAssignmentDialog";
import CrewAssignmentDialog from "../pages/crew-management/components/CrewAssignmentDialog";
import AssignmentReminderDialog from "../pages/crew-management/components/AssignmentReminderDialog";

interface INotificationData {
  id: string;
  type: string;
  user: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  action: string;
  target?: string;
  timestamp: string;
  isRead: boolean;
  record_id: number;
  record_data: {
    type: string;
    stream_id: number;
  };
}

const tabCategories = [
  { label: "All", icon: <Globe size={16} /> },
  { label: "Unread", icon: <MessageSquare size={16} /> },
  { label: "Read", icon: <Check size={16} /> },
];

export default function Notifications() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");

  const [selectedNotification, setSelectedNotification] = useState<number | null>(null);

  const [notifications, setNotifications] = useState<INotificationData[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showAssignmentReminderDialog, setShowAssignmentReminderDialog] =
    useState<boolean>(false);
  const [showProducerAssignmentDialog, setShowProducerAssignmentDialog] =
    useState<boolean>(false);
  const [showCrewAssignmentDialog, setShowCrewAssignmentDialog] =
    useState<boolean>(false);
  const [selectedStream, setSelectedStream] = useState<number | null>(null);

  // FETCH FIRST PAGE
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await apiService.get<any>(
          "/api/notifications/notification/"
        );

        const raw = res.data.results || res.data || [];

        const mapped = raw.map((item: any) => ({
          id: String(item.id),
          type: item.database_table || "general",
          user: {
            name: item.created_by_username || "Unknown User",
            avatar: "",
          },
          action: item.title || "Notification",
          target: item.description || "",
          due_date_time: item.due_date_time,
          timestamp: new Date(item.created_at).toLocaleString(),
          isRead: item.status === "read",
          record_id: item.object_id,
          record_data: item.related_object_data,
        }));

        setNotifications(mapped);
        setNextPageUrl(res.data.next || null);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    fetchNotifications();
  }, []);

  const loadMoreNotifications = async () => {
    if (!nextPageUrl) return;

    setLoadingMore(true);

    try {
      const res = await apiService.get<any>(nextPageUrl);

      const raw = res.data.results || [];

      const mapped = raw.map((item: any) => ({
        id: String(item.id),
        type: item.database_table || "general",
        user: {
          name: item.created_by_username || "Unknown User",
          avatar: "",
        },
        action: item.title || "Notification",
        target: item.description || "",
        due_date_time: item.due_date_time,
        timestamp: new Date(item.created_at).toLocaleString(),
        isRead: item.status === "read",
        record_id: item.object_id,
        record_data: item.related_object_data,
      }));

      setNotifications((prev) => [...prev, ...mapped]);
      setNextPageUrl(res.data.next || null);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    }

    setLoadingMore(false);
  };

  // TAB FILTERS
  const filteredNotifications = useMemo(() => {
    if (activeTab === "Unread") return notifications.filter((n) => !n.isRead);
    if (activeTab === "Read") return notifications.filter((n) => n.isRead);
    return notifications;
  }, [activeTab, notifications]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  const handleToggleReadStatus = async (id: string) => {
    try {
      await apiService.patch(`/api/notifications/notification/${id}/`, {
        status: "read",
      });
    } catch (err) {
      console.error("Failed to update:", err);
    }

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: !n.isRead } : n))
    );
  };

  const handleRemoveNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <>
      <IconButton onClick={() => setOpen(true)} sx={{ position: "relative" }}>
        <Badge badgeContent={unreadCount} color="error">
          <Bell size={22} />
        </Badge>
      </IconButton>

      {/* POPUP */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: "absolute",
              top: "60px",
              right: "20px",
              width: "420px",
              zIndex: 2000,
            }}
          >
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 6,
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                {/* HEADER */}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">
                    Notifications
                  </Typography>
                  <IconButton onClick={() => setOpen(false)}>
                    <X size={20} />
                  </IconButton>
                </Box>

                {/* TABS */}
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {tabCategories.map((tab) => (
                    <Chip
                      key={tab.label}
                      label={tab.label}
                      icon={tab.icon}
                      clickable
                      color={activeTab === tab.label ? "primary" : "default"}
                      onClick={() => setActiveTab(tab.label)}
                    />
                  ))}
                </Box>

                {/* NOTIFICATIONS */}
                <Box
                  sx={{ maxHeight: 400, zIndex: 10, overflowY: "auto", mt: 2 }}
                >
                  <AnimatePresence mode="popLayout">
                    {filteredNotifications.length > 0 ? (
                      filteredNotifications.map((n) => {
                        return (
                          <motion.div
                            key={n.id}
                            style={{
                              zIndex: 10
                            }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                mb: 2,
                                zIndex: 10,
                                bgcolor: n.isRead ? "grey.100" : "#f0f4f8",
                              }}
                            >
                              {/* Notification Title as Hyperlink */}
                              <Typography
                                component="a"
                                href={n.record_id && n.action.toLowerCase().includes("stream") ?
                                  n.record_data?.type === 'call_sheet' ? `/stream-management/view/${n.record_data?.stream_id}`:
                                    `/stream-management/view/${n.record_id}` :
                                  n.record_id && (n.action.toLowerCase().includes("booking") && !n.action.includes("quote")) ?
                                    `/bookings/${n.record_id}` :
                                  n.record_id && n.action.toLowerCase().includes("venue") ?
                                    `/venues/view/${n.record_id}` : undefined
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                fontWeight="bold"
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                  cursor: "pointer",
                                }}
                              >
                                {n.action}
                              </Typography>

                              {n.target && (
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.5, whiteSpace: "pre-line" }}
                                >
                                  {n.target}
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: "block" }}
                              >
                                {n.timestamp}
                              </Typography>

                              <Box sx={{ display: "flex", alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                {/* VIEW STREAM */}
                                {n.record_id && (n.action.toLowerCase().includes("new stream created") ||
                                  n.action.toLowerCase().includes("new producer assignment") ||
                                    n.action.toLowerCase().includes("new stream scheduled")) ? (
                                    <Chip
                                      component="a"
                                      clickable
                                      label="View Stream"
                                      href={`/stream-management/view/${n.record_data?.type === 'call_sheet' ? n.record_data?.stream_id : n.record_id}`}
                                      sx={{
                                        bgcolor: "#E0F2FE",
                                        color: "#0369A1",
                                        fontWeight: 600,
                                      }}
                                    />
                                  ) : null}

                                {/* ASSIGN PRODUCER */}
                                {n.record_id && (n.action.toLowerCase().includes("new stream created") || 
                                  n.action.toLowerCase().includes("new stream scheduled")) ? (
                                  <Chip
                                    component="a"
                                    clickable
                                    label="Assign Producer"
                                    onClick={() => {
                                      setSelectedStream(n.record_id);
                                      setShowProducerAssignmentDialog(true);
                                    }}
                                    sx={{
                                      bgcolor: "#DCFCE7",
                                      color: "#15803D",
                                      fontWeight: 600,
                                    }}
                                  />
                                ) : null}

                                {/* ASSIGN CREW */}
                                {n.record_id && n.action.toLowerCase().includes("new producer assignment") ? (
                                  <Chip
                                    component="a"
                                    clickable
                                    label="Assign Crew"
                                    onClick={() => {
                                      setSelectedStream(n.record_data?.stream_id);
                                      setShowCrewAssignmentDialog(true);
                                    }}
                                    sx={{
                                      bgcolor: "#DCFCE7",
                                      color: "#15803D",
                                      fontWeight: 600,
                                    }}
                                  />
                                ): null}

                                {/* REMIND LATER */}
                                {n.record_id && (n.action.toLowerCase().includes("new stream created") ||
                                  n.action.toLowerCase().includes("new producer assignment") ||
                                    n.action.toLowerCase().includes("new stream scheduled")) ? (
                                  <Chip
                                    component="a"
                                    clickable
                                    label="Remind Later"
                                    onClick={() => {
                                      setSelectedNotification(parseInt(n.id));
                                      setSelectedStream(n.record_data?.type === 'call_sheet' ? n.record_data?.stream_id : n.record_id);
                                      setShowAssignmentReminderDialog(true);
                                    }}
                                    sx={{
                                      bgcolor: "#F79E1B20",
                                      color: "#F79E1B",
                                      fontWeight: 600,
                                    }}
                                  />
                                ) : null}

                                {/* VIEW BOOKING */}
                                {n.record_id && (n.action.toLowerCase().includes("new booking") &&
                                  !n.action.toLowerCase().includes("quote request")) ? (
                                    <Chip
                                      component="a"
                                      clickable
                                      label="View Booking"
                                      href={`/bookings/${n.record_id}`}
                                      sx={{
                                        bgcolor: "#DCFCE7",
                                        color: "#15803D",
                                        fontWeight: 600,
                                      }}
                                    />
                                  ) : null}

                                {/* VIEW VENUE */}
                                {n.type === "venue_venue" && (
                                  <Chip
                                    component="a"
                                    clickable
                                    label="View Venue"
                                    href={`/venues/view/${n.record_id}`}
                                    sx={{
                                      bgcolor: "#FEE2E2",
                                      color: "#B91C1C",
                                      fontWeight: 600,
                                    }}
                                  />
                                )}

                                {/* PREVIEW QUOTATION DETAILS */}
                                {n.record_id && (n.action.toLowerCase().includes("new booking quote request") ||
                                  n.action.toLowerCase().includes("quotation ready")) ? (
                                  <Chip
                                    component="a"
                                    clickable
                                    label="Preview Quotation Details"
                                    href={`/request-for-quote/${n.record_id}`}
                                    sx={{
                                      bgcolor: "#DCFCE7",
                                      color: "#15803D",
                                      fontWeight: 600,
                                    }}
                                  />
                                ) : null}

                                <Box sx={{ display: "flex", gap: 1 }}>
                                  {/* MARK READ */}
                                  <Tooltip title="Mark as read">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleReadStatus(n.id);
                                      }}
                                    >
                                      <Check size={18} />
                                    </IconButton>
                                  </Tooltip>

                                  {/* DELETE */}
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveNotification(n.id);
                                    }}
                                  >
                                    <X size={18} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>
                          </motion.div>
                        );
                      })
                    ) : (
                      <Box textAlign="center" py={4} color="text.secondary">
                        No notifications found.
                      </Box>
                    )}
                  </AnimatePresence>

                  {/* LOAD MORE BUTTON */}
                  {nextPageUrl && (
                    <Box textAlign="center" mt={2} mb={2}>
                      <Button
                        onClick={loadMoreNotifications}
                        disabled={loadingMore}
                        variant="outlined"
                      >
                        {loadingMore ? (
                          <CircularProgress size={18} />
                        ) : (
                          "Load More"
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        )}
        {/* DIALOGS */}

        <AssignmentReminderDialog
          notification={selectedNotification}
          setShowAssignmentReminderDialog={setShowAssignmentReminderDialog}
          showAssignmentReminderDialog={showAssignmentReminderDialog}
          selectedStream={selectedStream}
        />

        <ProducerAssignmentDialog
          setShowProducerAssignmentDialog={setShowProducerAssignmentDialog}
          showProducerAssignmentDialog={showProducerAssignmentDialog}
          selectedStream={selectedStream}
        />

        <CrewAssignmentDialog
          setShowCrewAssignmentDialog={setShowCrewAssignmentDialog}
          showCrewAssignmentDialog={showCrewAssignmentDialog}
          selectedStream={selectedStream}
        />
      </AnimatePresence>
    </>
  );
}
